import { forwardRef, type PropsWithChildren, useRef } from 'react';

import { XYChartImperativeHandlerBuilder } from './builders/XYChartImperativeHandlerBuilder.js';
import { XYChartAnnotationsArea } from './components/annotations/XYChartAnnotationsArea.js';
import { XYChartAnnotationsIndicators } from './components/annotations/XYChartAnnotationsIndicators.js';
import { XYChartBottomAxisArea } from './components/axes/XYChartBottomAxisArea.js';
import { XYChartLeftAxisArea } from './components/axes/XYChartLeftAxisArea.js';
import { XYChartRightAxisArea } from './components/axes/XYChartRightAxisArea.js';
import { XYChartTopAxisArea } from './components/axes/XYChartTopAxisArea.js';
import { BarLabelLayout } from './components/displays/BarDisplay/BarLabelLayout.js';
import { XYChartGridLayout } from './components/layout/XYChartGridLayout.js';
import { XYChartOverlay } from './components/overlay/XYChartOverlay.js';
import { XYChartThresholdArea } from './components/thresholds/XYChartThresholdArea.js';
import {
  ANNOTATIONS_PADDING_BOTTOM,
  XY_CHART_ARIA_LABEL,
} from './constants/defaults.js';
import { XYChartSelectConfigContext } from './context/XYChartSelectConfig.context.js';
import { XYChartTooltipContext } from './context/XYChartTooltip.context.js';
import { useVisibleAxesConfig } from './hooks/useVisibleAxesConfig.js';
import { useXYChartAnnotationsConfig } from './hooks/useXYChartAnnotationsConfig.js';
import { useXYChartAnnotationsData } from './hooks/useXYChartAnnotationsData.js';
import { useXYChartAnnotationsHiddenTracks } from './hooks/useXYChartAnnotationsHiddenTracks.js';
import { useXYChartData } from './hooks/useXYChartData.js';
import { XYChartAxisBorder } from './layout/XYChartAxisBorder.js';
import { XYChartLayout } from './layout/XYChartLayout.js';
import { XYChartProviders } from './providers/XYChartProviders.js';
import { XYChartRef } from './types/xy-chart.js';
import { getPaddingByAxes } from './utils/get-padding-by-axes.js';
import {
  gridTemplateAreas,
  gridTemplateColumns,
  gridTemplateRows,
} from './utils/grid-templates.js';
import type { XYChartConfig } from './utils/xy-chart-configuration.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { defaultChartSize } from '../../core/styles/defaultChartValues.sty.js';
import { getAnnotationsVisibleArea } from '../core/components/annotations/utils/get-annotations-visible-area.js';
import { ChartGrid } from '../core/components/chart-grid/ChartGridRoot.js';
import {
  shouldShowAnnotations,
  shouldShowAxes,
} from '../core/components/chart-grid/constants.js';
import { useChartLayoutLoading } from '../core/components/chart-layout/hooks/useChartLayoutLoading.js';
import { useGraphSize } from '../core/components/chart-layout/hooks/useGraphSize.js';
import { ChartLoaderOverlay } from '../core/components/chart-loader-overlay/ChartLoaderOverlay.js';

interface XYChartProps {
  config: XYChartConfig;
}

export const XYChart = forwardRef<XYChartRef, PropsWithChildren<XYChartProps>>(
  ({ config }, forwardedRef) => {
    const loading = useChartLayoutLoading();
    const { width, height } = useGraphSize();
    const chartRef = useRef<HTMLDivElement>(null);
    const chartAndForwardedRef = useMergeRefs<XYChartRef | HTMLDivElement>([
      forwardedRef,
      chartRef,
    ]);
    const { top, bottom, left, right } = useVisibleAxesConfig();
    const { axesMetadata } = useXYChartData();

    const topAxisMaxSize = top?.tickLabelMaxSize;
    const bottomAxisMaxSize = bottom?.tickLabelMaxSize;
    const leftAxisMaxSize = left?.tickLabelMaxSize;
    const rightAxisMaxSize = right?.tickLabelMaxSize;
    const paddings = getPaddingByAxes({ top, bottom, left, right });
    const chartGridHeight = height - paddings.vertical();
    const chartGridWidth = width - paddings.horizontal();

    const annotationsData = useXYChartAnnotationsData();
    const annotationsConfig = useXYChartAnnotationsConfig();
    const tracksQuantity = annotationsData?.size ?? 0;
    const hiddenTracks = useXYChartAnnotationsHiddenTracks();

    const annotationsVisibleArea = getAnnotationsVisibleArea(
      tracksQuantity,
      annotationsConfig?.fixedTracks,
      hiddenTracks.length,
    );

    const hasVisibleAnnotationTracks = tracksQuantity - hiddenTracks.length > 0;
    const annotationsPadding = hasVisibleAnnotationTracks
      ? ANNOTATIONS_PADDING_BOTTOM
      : 0;
    const annotationsAreaHeight = annotationsVisibleArea + annotationsPadding;

    const showAnnotations = shouldShowAnnotations(
      chartGridHeight,
      tracksQuantity,
    );

    const showAxes = shouldShowAxes(chartGridHeight);

    const gridAnnotationsHeight = showAnnotations ? annotationsAreaHeight : 0;
    const computedGridTemplateRows = showAxes
      ? gridTemplateRows(
          bottomAxisMaxSize,
          topAxisMaxSize,
          gridAnnotationsHeight,
        )
      : '0px 0px 1fr 1px';

    return (
      <div
        className={defaultChartSize}
        style={{
          paddingTop: paddings.top,
          paddingLeft: paddings.left,
        }}
        aria-label={XY_CHART_ARIA_LABEL}
        role="img"
      >
        <ChartGrid
          width={chartGridWidth}
          height={chartGridHeight}
          gridTemplateAreas={gridTemplateAreas}
          gridTemplateColumns={gridTemplateColumns(
            leftAxisMaxSize,
            rightAxisMaxSize,
          )}
          gridTemplateRows={computedGridTemplateRows}
          ref={chartAndForwardedRef}
        >
          <XYChartTooltipContext.Provider value={config.tooltip}>
            <XYChartSelectConfigContext.Provider value={config.select}>
              <XYChartProviders>
                <XYChartImperativeHandlerBuilder
                  chartRef={forwardedRef}
                  containerRef={chartRef}
                  config={config}
                >
                  {showAxes && <XYChartTopAxisArea />}
                  {showAxes && <XYChartBottomAxisArea />}
                  {showAxes && <XYChartLeftAxisArea />}
                  {showAxes && <XYChartRightAxisArea />}

                  {showAnnotations && <XYChartAnnotationsArea />}
                  <ChartGrid.PlotArea>
                    <XYChartGridLayout />
                    <XYChartLayout />
                    <BarLabelLayout />
                    <XYChartAxisBorder />
                    {showAnnotations && <XYChartAnnotationsIndicators />}
                    <ChartLoaderOverlay
                      loading={loading}
                      zoomEnabled={axesMetadata.isZoomEnabled}
                    >
                      <XYChartOverlay />
                    </ChartLoaderOverlay>
                  </ChartGrid.PlotArea>
                  <XYChartThresholdArea />
                </XYChartImperativeHandlerBuilder>
              </XYChartProviders>
            </XYChartSelectConfigContext.Provider>
          </XYChartTooltipContext.Provider>
        </ChartGrid>
      </div>
    );
  },
);

(XYChart as typeof XYChart & { displayName: string }).displayName = 'XYChart';
